import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { tap, catchError, shareReplay } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User } from '../models';

const ACCESS_KEY   = 'pms_access_token';
const REFRESH_KEY  = 'pms_refresh_token';
const EXPIRES_KEY  = 'pms_access_token_expires_at';
const PROACTIVE_MS = 60_000; // refresh 1 minute before expiry

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  readonly currentUser = signal<User | null>(null);

  // Shared observable so concurrent 401s don't each fire a separate refresh call.
  private refreshInFlight: Observable<{ accessToken: string; refreshToken: string; accessTokenExpiresAt?: string }> | null = null;
  private proactiveTimer: ReturnType<typeof setTimeout> | null = null;

  login(email: string, password: string): Observable<void> {
    return this.http
      .post<{ accessToken: string; refreshToken: string; accessTokenExpiresAt?: string; user: User }>(
        `${environment.apiUrl}/auth/login`,
        { email, password }
      )
      .pipe(
        tap((res) => {
          localStorage.setItem(ACCESS_KEY, res.accessToken);
          localStorage.setItem(REFRESH_KEY, res.refreshToken);
          if (res.accessTokenExpiresAt) {
            localStorage.setItem(EXPIRES_KEY, res.accessTokenExpiresAt);
          }
          this.currentUser.set(res.user);
          this.scheduleProactiveRefresh();
        })
      ) as unknown as Observable<void>;
  }

  logout(): Observable<void> {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    const request$ = refreshToken
      ? this.http.post<void>(`${environment.apiUrl}/auth/logout`, { refreshToken })
      : of(undefined);

    return request$.pipe(
      tap(() => {
        this.clearTokens();
        this.router.navigate(['/login']);
      })
    );
  }

  refreshToken(): Observable<{ accessToken: string; refreshToken: string; accessTokenExpiresAt?: string }> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    const refreshToken = localStorage.getItem(REFRESH_KEY);
    this.refreshInFlight = this.http
      .post<{ accessToken: string; refreshToken: string; accessTokenExpiresAt?: string }>(
        `${environment.apiUrl}/auth/refresh`,
        { refreshToken }
      )
      .pipe(
        tap((res) => {
          localStorage.setItem(ACCESS_KEY, res.accessToken);
          localStorage.setItem(REFRESH_KEY, res.refreshToken);
          if (res.accessTokenExpiresAt) {
            localStorage.setItem(EXPIRES_KEY, res.accessTokenExpiresAt);
          }
          this.refreshInFlight = null;
          this.scheduleProactiveRefresh();
        }),
        catchError((err) => {
          this.refreshInFlight = null;
          return throwError(() => err);
        }),
        shareReplay(1),
      );

    return this.refreshInFlight;
  }

  scheduleProactiveRefresh(): void {
    if (this.proactiveTimer) {
      clearTimeout(this.proactiveTimer);
      this.proactiveTimer = null;
    }

    const expiresAt = localStorage.getItem(EXPIRES_KEY);
    if (!expiresAt) return;

    const delay = new Date(expiresAt).getTime() - Date.now() - PROACTIVE_MS;

    if (delay <= 0) {
      // Already within the 1-minute window — refresh immediately
      this.refreshToken().subscribe({
        error: () => {
          this.clearTokens();
          this.router.navigate(['/login']);
        },
      });
      return;
    }

    this.proactiveTimer = setTimeout(() => {
      this.proactiveTimer = null;
      this.refreshToken().subscribe({
        error: () => {
          this.clearTokens();
          this.router.navigate(['/login']);
        },
      });
    }, delay);
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem(ACCESS_KEY);
  }

  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_KEY);
  }

  clearTokens(): void {
    if (this.proactiveTimer) {
      clearTimeout(this.proactiveTimer);
      this.proactiveTimer = null;
    }
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(EXPIRES_KEY);
    this.currentUser.set(null);
  }

  loadCurrentUser(): Observable<User> {
    return this.http.get<User>(`${environment.apiUrl}/users/me`).pipe(
      tap((user) => this.currentUser.set(user))
    );
  }

  // Route guards read `currentUser()` synchronously to make role-based
  // decisions (e.g. redirecting HR users out of the dashboard). On a fresh
  // page reload the signal starts out null and is only populated once
  // loadCurrentUser() resolves, so a guard evaluated in that window sees no
  // role and lets navigation through it shouldn't. Guards should await this
  // instead of reading currentUser() directly.
  ensureCurrentUser(): Observable<User | null> {
    const existing = this.currentUser();
    if (existing) return of(existing);
    if (!this.isAuthenticated()) return of(null);
    return this.loadCurrentUser().pipe(catchError(() => of(null)));
  }
}
